import { Card } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

import type { HealthStatus } from '@/api/portfolio/api';
import Text from '@/atoms/Text';
import { HealthDot } from '@/components/common/HealthIndicator';
import { Pill, type PillTone } from '@/components/common/Pill';
import type { HealthStatusType } from '@/components/project_details_components/HealthStatusBadge';

const statusToApiStatus = (status: string): HealthStatus => {
  return status.toLowerCase() as HealthStatus;
};

const getStatusTone = (status: string): PillTone => {
  const normalized = status.toLowerCase();
  switch (normalized) {
    case 'green':
      return 'success';
    case 'yellow':
      return 'warning';
    case 'red':
      return 'error';
    case 'pending':
      return 'info';
    default:
      return 'info';
  }
};

const getStatusLabel = (status: string): string => {
  const normalized = status.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export interface HealthItem {
  status: string;
  title: string;
  description: string;
  isComingSoon?: boolean;
}

export interface HealthCardProps {
  items: HealthItem[];
  healthCard?: {
    rollupStatus: HealthStatusType;
    rollupLabel: string;
    updatedBy?: string | null;
    /** ISO date of the weekly update the health indicators come from; omitted when none exists. */
    updatedAt?: string | null;
  };
  onEdit?: () => void;
  canViewHealthDetails?: boolean;
}

export function HealthCard({
  items,
  healthCard,
  onEdit: _onEdit,
  canViewHealthDetails = false,
}: Readonly<HealthCardProps>): React.ReactElement {
  const healthIndicatorItems = items.slice(0, 4); // First 4: health indicators
  const narrativeItems = items.slice(4, 8); // Last 4: narrative items (Notes, Accomplishments, etc.)
  const notesItem = narrativeItems.length > 0 ? narrativeItems[0] : null;
  const otherNarrativeItems = narrativeItems.slice(1);

  return (
    <Card className="health-card">
      <div className="card__header">
        <div className="health-card__title-group">
          <Text variant="h5">Health</Text>
          {healthCard?.updatedAt && (
            <Text variant="bodySmall" color="var(--text-secondary)">
              Last updated on {dayjs(healthCard.updatedAt).format('YYYY-M-D')}
            </Text>
          )}
        </div>
        <Pill tone="neutral">Source: Manual</Pill>
      </div>

      {/* Top section: Indicators (left) + Notes (right) */}
      <div className="health-card__top-section">
        {/* Health Indicators Card (left side) - ALWAYS VISIBLE */}
        {healthIndicatorItems && healthIndicatorItems.length > 0 && (
          <Card className="health-card__indicators-card">
            <div className="health-card__indicators">
              {healthIndicatorItems.map((item) => (
                <div key={item.title} className="health-card__indicator-row">
                  {item.isComingSoon ? (
                    <div className="health-card__item-badge">Coming in V2</div>
                  ) : (
                    <Pill
                      tone={getStatusTone(item.status)}
                      leading={<HealthDot status={statusToApiStatus(item.status)} />}
                    >
                      {getStatusLabel(item.status)}
                    </Pill>
                  )}
                  <Text variant="bodyDefault">{item.title}</Text>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Notes Card (right side) - CONDITIONAL: Only show if user can view health details */}
        {canViewHealthDetails && notesItem && (
          <Card className="health-card__notes-card">
            <Text variant="bodyDefaultStrong" className="health-card__narrative-label">
              {notesItem.title}
            </Text>
            <div className="health-card__notes-content">
              <svg
                className="health-card__notes-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <Text variant="bodyDefault">{notesItem.status || 'No notes'}</Text>
            </div>
          </Card>
        )}
      </div>

      {/* Narrative sections below (full width cards) - CONDITIONAL: Only show if user can view health details */}
      {canViewHealthDetails && otherNarrativeItems && otherNarrativeItems.length > 0 && (
        <div className="health-card__narrative-sections">
          {otherNarrativeItems.map((item) => (
            <Card key={item.title} className="health-card__narrative-card">
              <Text variant="bodyDefaultStrong" className="health-card__narrative-label">
                {item.title}
              </Text>
              <Text variant="bodyDefault" className="health-card__narrative-content">
                {item.status || 'No data available'}
              </Text>
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
}
